import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, lastValueFrom } from 'rxjs';
import { addIcons } from 'ionicons';
import {
  batteryChargingOutline,
  calculatorOutline,
  close,
  documentTextOutline,
  logOutOutline,
  homeOutline,
  menuOutline,
  newspaperOutline,
  saveOutline,
  shieldCheckmarkOutline,
  statsChartOutline,
  sunnyOutline,
  timeOutline,
  warningOutline,
  flashOutline,
} from 'ionicons/icons';
import { environment } from 'src/environments/environment';
import { SharedModule } from '../../shared/shared.module';
import { PublicUrlTranslationService } from '../../shared/services/public-url-translation.service';
import { TranslationService, LanguageItem } from '../../shared/services/translation.service';
import { parseQueryParams } from '../../shared/utils/parse-query-params';
import { AuthService } from '../../shared/services';
import { ToolLinksService } from '../../shared/services/tool-links.service';
import { CncsWizardService } from '../../pages/wizards/cncs-wizard/cncs-wizard/cncs-wizard.service';
import { SsdgWizardService } from '../../pages/wizards/ssdg-wizard/ssdg-wizard.service';
import { MsdgWizardService } from '../../pages/wizards/msdg-wizard/msdg-wizard.service';
import { AgrivoltaicOneWizardService } from '../../pages/wizards/agrivoltaic-one-wizard/agrivoltaic-one-wizard.service';
import { AgrivoltaicTwoWizardService } from '../../pages/wizards/agrivoltaic-two-wizard/agrivoltaic-two-wizard.service';
import { SimulationWizardService } from '../../pages/simulation/simulation-wizard.service';
import { ImpersonationBannerComponent } from '../impersonation-banner/impersonation-banner.component';
import { CebSourceIconComponent } from '../ceb-source-icon/ceb-source-icon.component';

type NavCardLink = {
  path: string;
  labelKey?: string;
  titleKey: string;
  subtitleKey: string;
  icon?: 'file-text' | 'sun' | 'battery' | 'calculator' | 'newspaper' | 'zap';
};

type NavSection = {
  key: 'schemes' | 'tools' | 'resources';
  labelKey: string;
  menuWidth: number;
  links: NavCardLink[];
};

type QuickLink = {
  labelKey: string;
  path: string;
  icon: 'triangle-alert' | 'shield-alert' | 'chart-no-axes-column' | 'zap';
};

type HoverSection = NavSection['key'] | 'languages' | 'profile';

@Component({
  selector: 'app-ceb-wizard-header',
  standalone: true,
  imports: [SharedModule, ImpersonationBannerComponent, CebSourceIconComponent],
  templateUrl: './ceb-wizard-header.component.html',
  styleUrls: ['./ceb-wizard-header.component.scss'],
})
export class CebWizardHeaderComponent implements AfterViewInit {
  private static readonly FALLBACK_LANGUAGES: LanguageItem[] = [
    {
      id: 1,
      createdAt: '',
      updatedAt: '',
      disabledAt: null,
      active: true,
      languageFull: 'French',
      languageFullNative: 'Francais',
      languageISO2: 'fr',
      languageISO3: 'fra',
      flagImage: '',
      flagCode: 'fr',
      isBaseLanguage: false,
      isPopularLanguage: true,
    },
    {
      id: 2,
      createdAt: '',
      updatedAt: '',
      disabledAt: null,
      active: true,
      languageFull: 'English',
      languageFullNative: 'English',
      languageISO2: 'en',
      languageISO3: 'eng',
      flagImage: '',
      flagCode: 'gb',
      isBaseLanguage: true,
      isPopularLanguage: true,
    },
    {
      id: 3,
      createdAt: '',
      updatedAt: '',
      disabledAt: null,
      active: true,
      languageFull: 'Kreol',
      languageFullNative: 'Kreol',
      languageISO2: 'mfe',
      languageISO3: 'mfe',
      flagImage: '',
      flagCode: 'mu',
      isBaseLanguage: false,
      isPopularLanguage: true,
    },
  ];

  readonly navSections: NavSection[] = [
    {
      key: 'schemes',
      labelKey: 'shared.ceb.header.sections.schemes',
      menuWidth: 340,
      links: [
        { path: '/ssdg', labelKey: 'shared.ceb.header.links.schemes.ssdg.label', titleKey: 'shared.ceb.header.links.schemes.ssdg.title', subtitleKey: 'shared.ceb.header.links.schemes.ssdg.subtitle' },
        { path: '/msdg', labelKey: 'shared.ceb.header.links.schemes.msdg.label', titleKey: 'shared.ceb.header.links.schemes.msdg.title', subtitleKey: 'shared.ceb.header.links.schemes.msdg.subtitle' },
        { path: '/cncs', labelKey: 'shared.ceb.header.links.schemes.cncs.label', titleKey: 'shared.ceb.header.links.schemes.cncs.title', subtitleKey: 'shared.ceb.header.links.schemes.cncs.subtitle' },
        // { path: '/cav1', labelKey: 'shared.ceb.header.links.schemes.cav1.label', titleKey: 'shared.ceb.header.links.schemes.cav1.title', subtitleKey: 'shared.ceb.header.links.schemes.cav1.subtitle' },
        { path: '/cav2', labelKey: 'shared.ceb.header.links.schemes.cav2.label', titleKey: 'shared.ceb.header.links.schemes.cav2.title', subtitleKey: 'shared.ceb.header.links.schemes.cav2.subtitle' },
      ],
    },
    {
      key: 'tools',
      labelKey: 'shared.ceb.header.sections.tools',
      menuWidth: 280,
      links: [
        { path: '/tools/form', titleKey: 'shared.ceb.header.links.tools.form.title', subtitleKey: 'shared.ceb.header.links.tools.form.subtitle', icon: 'file-text' },
        { path: '/tools/productivity', titleKey: 'shared.ceb.header.links.tools.productivity.title', subtitleKey: 'shared.ceb.header.links.tools.productivity.subtitle', icon: 'sun' },
        { path: '/tools/sizing', titleKey: 'shared.nav.tool_items.dimensionnement.title', subtitleKey: 'shared.nav.tool_items.dimensionnement.description', icon: 'calculator' },
        { path: '/tools/solar-batteries', titleKey: 'shared.nav.tool_items.batteries.title', subtitleKey: 'shared.nav.tool_items.batteries.description', icon: 'battery' },
      ],
    },
    {
      key: 'resources',
      labelKey: 'shared.ceb.header.sections.resources',
      menuWidth: 280,
      links: [
        { path: '/ressources/brochures', titleKey: 'shared.ceb.header.links.resources.brochures.title', subtitleKey: 'shared.ceb.header.links.resources.brochures.subtitle', icon: 'file-text' },
        { path: '/ressources/communiques', titleKey: 'shared.ceb.header.links.resources.releases.title', subtitleKey: 'shared.ceb.header.links.resources.releases.subtitle', icon: 'newspaper' },
        { path: '/ressources/gridcodes', titleKey: 'shared.ceb.header.links.resources.grid_codes.title', subtitleKey: 'shared.ceb.header.links.resources.grid_codes.subtitle', icon: 'zap' },
      ],
    },
  ];

  readonly quickLinks: QuickLink[] = [
    { labelKey: 'shared.ceb.header.quick_links.scam_alert', path: '/scam-alert', icon: 'triangle-alert' },
    { labelKey: 'shared.ceb.header.quick_links.standards', path: '/installer-standards', icon: 'shield-alert' },
    { labelKey: 'shared.ceb.header.quick_links.pvgis', path: '/pvgis-productible', icon: 'chart-no-axes-column' },
    { labelKey: 'shared.nav.electricity_prices', path: '/electricity-prices-in-mauritius', icon: 'zap' },
  ];

  readonly languages = signal<LanguageItem[]>([]);
  readonly showManagerStatusButton = computed(
    () =>
      this.authService.isManager() &&
      (
        this.cncsWizardService.applicationStatusIsCompleted() ||
        this.ssdgWizardService.applicationStatusIsCompleted() ||
        this.msdgWizardService.applicationStatusIsCompleted() ||
        this.agrivoltaicOneWizardService.applicationStatusIsCompleted() ||
        this.agrivoltaicTwoWizardService.applicationStatusIsCompleted()
      )
  );
  readonly showSaveButton = computed(
    () =>
      this.cncsWizardService.showSaveInHeader() ||
      this.ssdgWizardService.showSaveInHeader() ||
      this.msdgWizardService.showSaveInHeader() ||
      this.agrivoltaicOneWizardService.showSaveInHeader() ||
      this.agrivoltaicTwoWizardService.showSaveInHeader() ||
      this.simulationWizardService.showSaveInHeader()
  );
  readonly showUnsavedIndicator = computed(
    () =>
      this.cncsWizardService.showUnsavedIndicator() ||
      this.ssdgWizardService.showUnsavedIndicator() ||
      this.msdgWizardService.showUnsavedIndicator() ||
      this.agrivoltaicOneWizardService.showUnsavedIndicator() ||
      this.agrivoltaicTwoWizardService.showUnsavedIndicator() ||
      this.simulationWizardService.showUnsavedIndicator()
  );

  mobileMenuOpen = false;
  openSection: HoverSection | null = null;
  private hoverCloseTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private hoverClosingSection: HoverSection | null = null;
  private readonly hoverCloseDelayMs = 180;
  translatedUrls: Record<string, string> = {};
  private readonly defaultTopBarHeightPx = 64;
  private readonly translatablePaths = Array.from(
    new Set([
      '/',
      ...this.navSections.reduce<string[]>((paths, section) => {
        paths.push(...section.links.map((link) => link.path));
        return paths;
      }, []),
      ...this.quickLinks.map((link) => link.path),
    ]),
  );

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly elRef: ElementRef<HTMLElement>,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly toolLinksService: ToolLinksService,
    private readonly publicUrlTranslation: PublicUrlTranslationService,
    public readonly translation: TranslationService,
    private readonly cncsWizardService: CncsWizardService,
    private readonly ssdgWizardService: SsdgWizardService,
    private readonly msdgWizardService: MsdgWizardService,
    private readonly agrivoltaicOneWizardService: AgrivoltaicOneWizardService,
    private readonly agrivoltaicTwoWizardService: AgrivoltaicTwoWizardService,
    private readonly simulationWizardService: SimulationWizardService,
  ) {
    addIcons({
      'battery-charging-outline': batteryChargingOutline,
      'calculator-outline': calculatorOutline,
      close,
      'document-text-outline': documentTextOutline,
      'flash-outline': flashOutline,
      'home-outline': homeOutline,
      'log-out-outline': logOutOutline,
      'menu-outline': menuOutline,
      'newspaper-outline': newspaperOutline,
      'save-outline': saveOutline,
      'shield-checkmark-outline': shieldCheckmarkOutline,
      'stats-chart-outline': statsChartOutline,
      'sunny-outline': sunnyOutline,
      'time-outline': timeOutline,
      'warning-outline': warningOutline,
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.mobileMenuOpen = false;
        this.openSection = null;
      });

    this.translation
      .getUiLanguageChangeObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lang) => {
        void this.refreshTranslatedUrls(lang);
      });

    this.destroyRef.onDestroy(() => this.clearHoverCloseTimer());

    void this.loadLanguages();
    void this.refreshTranslatedUrls();
  }

  get currentLang(): string {
    const lang = this.translation.translate.currentLang ?? this.translation.translate.getCurrentLang();
    return (lang || 'fr').toUpperCase().slice(0, 2);
  }

  get currentLanguageIso2(): string {
    const lang = this.translation.translate.currentLang ?? this.translation.translate.getCurrentLang() ?? 'fr';
    const normalized = String(lang).toLowerCase();
    if (normalized === 'kr' || normalized === 'mu' || normalized === 'mfe') {
      return 'mfe';
    }
    return normalized.slice(0, 2) || 'fr';
  }

  get currentLanguageFlag(): string {
    return this.getFlagUrl(this.translation.translate.currentLang ?? this.translation.translate.getCurrentLang() ?? 'fr');
  }

  get publicBaseUrl(): string {
    return this.publicUrlTranslation.baseUrl;
  }

  get primaryNavSections(): NavSection[] {
    return this.navSections.filter((section) => section.key !== 'resources');
  }

  get resourcesNavSection(): NavSection | undefined {
    return this.navSections.find((section) => section.key === 'resources');
  }

  get saveButtonLabelKey(): string {
    return this.simulationWizardService.showSaveInHeader()
      ? 'shared.ceb.header.save_simulation'
      : 'shared.ceb.header.save_wizard';
  }

  get displayName(): string {
    const user = this.authService.getUserData();
    if (!user) {
      return 'User';
    }

    const firstName = String(user.first_name ?? '').trim();
    const lastName = String(user.last_name ?? '').trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || user.email || 'User';
  }

  get userInitials(): string {
    const user = this.authService.getUserData();
    if (!user) {
      return 'U';
    }

    const firstName = String(user.first_name ?? '').trim();
    const lastName = String(user.last_name ?? '').trim();

    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.trim().toUpperCase() || 'U';
    }

    const email = String(user.email ?? '').trim();
    return email.charAt(0).toUpperCase() || 'U';
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.clearHoverCloseTimer();
    this.openSection = null;
  }

  async ngAfterViewInit(): Promise<void> {
    this.updateTopBarHeightCssVar();
    const element = this.elRef.nativeElement;
    if (element && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.updateTopBarHeightCssVar());
      observer.observe(element);
      this.destroyRef.onDestroy(() => observer.disconnect());
    }
  }

  toggleSection(event: Event, section: HoverSection): void {
    event.preventDefault();
    event.stopPropagation();
    this.clearHoverCloseTimer();
    this.openSection = this.openSection === section ? null : section;
  }

  openSectionOnHover(section: HoverSection): void {
    this.clearHoverCloseTimer();
    this.openSection = section;
  }

  closeSectionOnLeave(section: HoverSection): void {
    this.clearHoverCloseTimer();
    this.hoverClosingSection = section;
    this.hoverCloseTimeoutId = setTimeout(() => {
      if (this.openSection === this.hoverClosingSection) {
        this.openSection = null;
      }
      this.hoverClosingSection = null;
      this.hoverCloseTimeoutId = null;
    }, this.hoverCloseDelayMs);
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    if (!this.mobileMenuOpen) {
      this.clearHoverCloseTimer();
      this.openSection = null;
    }
  }

  closeMobileMenu(): void {
    this.clearHoverCloseTimer();
    this.mobileMenuOpen = false;
    this.openSection = null;
  }

  onLanguageChange(lang: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.translation.setTranslationTo(lang);
    this.mobileMenuOpen = false;
    this.clearHoverCloseTimer();
    this.openSection = null;
  }

  onSave(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.cncsWizardService.showSaveInHeader()) {
      this.cncsWizardService.requestSave();
    } else if (this.ssdgWizardService.showSaveInHeader()) {
      this.ssdgWizardService.requestSave();
    } else if (this.msdgWizardService.showSaveInHeader()) {
      this.msdgWizardService.requestSave();
    } else if (this.agrivoltaicOneWizardService.showSaveInHeader()) {
      this.agrivoltaicOneWizardService.requestSave();
    } else if (this.agrivoltaicTwoWizardService.showSaveInHeader()) {
      this.agrivoltaicTwoWizardService.requestSave();
    } else if (this.simulationWizardService.showSaveInHeader()) {
      this.simulationWizardService.requestSave();
    }
  }

  openDashboardWeb(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const dashboardUrl = this.authService.getDashboardAutoLoginUrl('/dashboard');

    if (!dashboardUrl) {
      void this.router.navigate(['/login']);
      return;
    }

    window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
  }

  openManagerApplications(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const url = this.authService.getDashboardAutoLoginUrl('/applications', false);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  async onLogout(event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    this.clearHoverCloseTimer();
    this.openSection = null;
    await this.authService.logout();
    window.location.href = `${environment.cncsCoreUrl}/cross-logout`;
  }

  private clearHoverCloseTimer(): void {
    if (this.hoverCloseTimeoutId !== null) {
      clearTimeout(this.hoverCloseTimeoutId);
      this.hoverCloseTimeoutId = null;
    }
    this.hoverClosingSection = null;
  }

  absoluteUrl(path: string): string {
    const [basePath, ...hashParts] = path.split('#');
    const hashFragment = hashParts.length ? `#${hashParts.join('#')}` : '';
    const normalizedPath = this.publicUrlTranslation.normalizePath(basePath);
    const resolvedUrl = this.translatedUrls[normalizedPath] ?? this.publicUrlTranslation.buildFallbackUrl(normalizedPath);
    return `${resolvedUrl}${hashFragment}`;
  }

  resolveNavLink(path: string): string {
    return this.toolLinksService.resolveToolLink(path, (normalizedPath) =>
      this.absoluteUrl(normalizedPath),
    );
  }

  getFlagUrl(lang: string): string {
    const normalized = String(lang || 'fr').toLowerCase();
    if (normalized === 'fr') return 'https://flagcdn.com/w40/fr.png';
    if (normalized === 'en') return 'https://flagcdn.com/w40/gb.png';
    if (normalized === 'kr' || normalized === 'mu' || normalized === 'mfe') return 'https://flagcdn.com/w40/mu.png';
    return 'https://flagcdn.com/w40/fr.png';
  }

  private async loadLanguages(): Promise<void> {
    try {
      const languageList = await lastValueFrom(this.translation.getLanguages(parseQueryParams(
        {
          pageIndex: 1,
          pageSize: 100,
          sortField: 'createdAt',
          sortOrder: 'ascend',
          where: { active: true },
          searchOption: 'EXACT',
        },
        'ASC',
      )));

      this.languages.set(languageList.length ? languageList : CebWizardHeaderComponent.FALLBACK_LANGUAGES);
    } catch {
      this.languages.set(CebWizardHeaderComponent.FALLBACK_LANGUAGES);
    }

    const codes = this.languages().map((lang) => lang.languageISO2);
    if (codes.length) {
      this.translation.translate.addLangs(codes);
    }
  }

  private updateTopBarHeightCssVar(): void {
    const element = this.elRef.nativeElement;
    const height = element?.offsetHeight ?? element?.getBoundingClientRect?.()?.height ?? this.defaultTopBarHeightPx;
    document.documentElement.style.setProperty('--app-top-bar-height', `${Math.round(height)}px`);
  }

  private async refreshTranslatedUrls(lang?: string): Promise<void> {
    this.translatedUrls = await lastValueFrom(
      this.publicUrlTranslation.getTranslatedPublicUrls(this.translatablePaths, lang),
    );
  }
}
